import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";

import {
  getTimeLagList,
  updateTimeLagBulk,
} from "../api/timeLagService";

import { mapApiToForm } from "../utils/mapApiToForm";
import { buildTimeLagPayload } from "../utils/buildPayload";
import { TimeLagForm } from "../types/timeLag.types";
import { toast } from "react-toastify";

/* ===============================
   TEMP – replace later
=============================== */
const COMPANY_ID = 2;

const CompanySettings: React.FC = () => {
  const [formData, setFormData] = useState<TimeLagForm>({
    company: COMPANY_ID,

    glazing_rfq_time_lag: "",
    glazing_sales_team_time_lag: "",

    lixil_hatchway_time_lag: "",
    lixil_lixil_time_lag: "",

    hunter_douglas_rfq_time_lag: "",
    hunter_douglas_sales_team_time_lag: "",
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const isSingleDigit = (value: number | "") =>
    value === "" || (Number.isInteger(value) && value >= 0 && value <= 9);

  /* ===============================
     FETCH
  =============================== */
  useEffect(() => {
    fetchTimeLag();
  }, []);

  const fetchTimeLag = async () => {
    try {
      const res = await getTimeLagList(COMPANY_ID);

      if (res.data?.results?.length) {
        setFormData(prev => ({
          ...prev,
          ...mapApiToForm(res.data.results),
        }));
        setIsEditMode(false);
      } else {
        setIsEditMode(true);
      }
    } catch (e) {
      console.error("Failed to load time lag", e);
    }
  };

  /* ===============================
     HANDLERS
  =============================== */
  const handleChange = (
    field: keyof TimeLagForm,
    value: number | ""
  ) => {
    if (!isSingleDigit(value)) {
      toast.error("Please enter a single digit (0–9)");
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };


  const handleSave = async () => {
    setLoading(true);

    try {
      const payload = buildTimeLagPayload(formData, COMPANY_ID);
      await updateTimeLagBulk(payload);

      toast.success("Time lag updated successfully");
      fetchTimeLag();
      setIsEditMode(false);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Failed to update time lag";

      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };


  const handleCancel = () => {
    fetchTimeLag();
    toast.info("Changes discarded");
  };


  /* ===============================
     UI
  =============================== */
  return (
    <>
      <PageHeader
        title="Time Lag"
        breadcrumbs={[
          { label: "Dashboard", path: "/dashboard" },
          { label: "Time Lag" },
        ]}
      />

      <div className="container mt-4">
        <div className="card p-4">

          {/* EDIT BUTTON */}
          {!isEditMode && (
            <div className="text-end mb-3">
              <button
                type="button"
                className="btn btn-primary edit-icon-btn"
                // title="Edit Company"
                onClick={() => setIsEditMode(true)}
              >
                <i className="fas fa-edit text-white"></i>
              </button>
            </div>
          )}

          <div className="row">

            {/* ================= GLAZING ================= */}
            <div className="col-md-4 border-end">
              <h6 className="fw-bold mb-3">For Glazing</h6>

              <strong>RFQ</strong>
              <div className="d-flex align-items-center gap-2 mb-3">
                <span>Time lag :</span>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: 120 }}
                  disabled={!isEditMode}
                  value={formData.glazing_rfq_time_lag}
                  min={0}
                  max={9}
                  step={1}
                  onKeyDown={(e) => {
                    if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={e =>
                    handleChange(
                      "glazing_rfq_time_lag",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                />
                <small>In Days</small>
              </div>

              <strong>Sales Team</strong>
              <div className="d-flex align-items-center gap-2">
                <span>Time lag :</span>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: 120 }}
                  disabled={!isEditMode}
                  value={formData.glazing_sales_team_time_lag}
                  min={0}
                  max={9}
                  step={1}
                  onKeyDown={(e) => {
                    if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={e =>
                    handleChange(
                      "glazing_sales_team_time_lag",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                />
                <small>In Days</small>
              </div>
            </div>

            {/* ================= HUNTER DOUGLAS ================= */}
            <div className="col-md-4 border-end">
              <h6 className="fw-bold mb-3">For Hunter Douglas</h6>

              <strong>RFQ</strong>
              <div className="d-flex align-items-center gap-2 mb-3">
                <span>Time lag :</span>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: 120 }}
                  disabled={!isEditMode}
                  value={formData.hunter_douglas_rfq_time_lag}
                  min={0}
                  max={9}
                  step={1}
                  onKeyDown={(e) => {
                    if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={e =>
                    handleChange(
                      "hunter_douglas_rfq_time_lag",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                />
                <small>In Days</small>
              </div>

              <strong>Sales Team</strong>
              <div className="d-flex align-items-center gap-2">
                <span>Time lag :</span>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: 120 }}
                  disabled={!isEditMode}
                  value={formData.hunter_douglas_sales_team_time_lag}
                  min={0}
                  max={9}
                  step={1}
                  onKeyDown={(e) => {
                    if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={e =>
                    handleChange(
                      "hunter_douglas_sales_team_time_lag",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                />
                <small>In Days</small>
              </div>
            </div>

            {/* ================= LIXIL ================= */}
            <div className="col-md-4">
              <h6 className="fw-bold mb-3">For Lixil</h6>


              <strong>RFQ(Lixil)</strong>
              <div className="d-flex align-items-center gap-2 mb-3">
                <span>Time lag :</span>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: 120 }}
                  disabled={!isEditMode}
                  value={formData.lixil_lixil_time_lag}
                  min={0}
                  max={9}
                  step={1}
                  onKeyDown={(e) => {
                    if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={e =>
                    handleChange(
                      "lixil_lixil_time_lag",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                />
                <small>In Days</small>
              </div>
              
              <strong>Sales Team(Hatchway)</strong>
              <div className="d-flex align-items-center gap-2 ">
                <span>Time lag :</span>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: 120 }}
                  disabled={!isEditMode}
                  value={formData.lixil_hatchway_time_lag}
                  min={0}
                  max={9}
                  step={1}
                  onKeyDown={(e) => {
                    if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={e =>
                    handleChange(
                      "lixil_hatchway_time_lag",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                />
                <small>In Days</small>
              </div>
            </div>

          </div>

          {/* FOOTER */}
          {isEditMode && (
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn btn-dark"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default CompanySettings;
